import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './FootballIQTest.css';
import questionBankStatic from '../assets/question_bank.json';

interface Question {
  id: string;
  version: string;
  difficulty: number;
  topic: string;
  question: string;
  options: string[];
  correct: number;
  explanation: string;
  image?: string;
}

interface AnswerRecord {
  id: string;
  question: string;
  correct: boolean;
  difficulty: number;
  topic: string;
  iq_at_time: number;
}

const FootballIQTest: React.FC = () => {
  const navigate = useNavigate();
  const [gameState, setGameState] = useState<'loading' | 'test' | 'results'>('loading');
  const [playerName, setPlayerName] = useState('');
  // Test version removed - using all questions without version filtering
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [answeredQuestions, setAnsweredQuestions] = useState<AnswerRecord[]>([]);
  const [currentIQ, setCurrentIQ] = useState(60);
  const [confidence, setConfidence] = useState(0);
  const [questionPool, setQuestionPool] = useState<Question[]>([]);
  const [lastAnswerCorrect, setLastAnswerCorrect] = useState<boolean | null>(null);
  const [flaggedQuestions, setFlaggedQuestions] = useState<string[]>([]);
  const [questionBank, setQuestionBank] = useState<Question[]>([]);
  const [questionsLoaded, setQuestionsLoaded] = useState(false);

  // Load questions from API with fallback to static JSON
  useEffect(() => {
    const loadQuestions = async () => {
      try {
        const response = await fetch('/backend/api/get-questions.php');
        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data) && data.length > 0) {
            setQuestionBank(data);
            setQuestionsLoaded(true);
            console.log('Questions loaded from database:', data.length);
            return;
          }
        }
      } catch (error) {
        console.warn('Failed to load questions from API, using static JSON:', error);
      }
      
      // Fallback to static JSON
      setQuestionBank(questionBankStatic as Question[]);
      setQuestionsLoaded(true);
      console.log('Questions loaded from static JSON:', questionBankStatic.length);
    };
    
    loadQuestions();
  }, []);

  // Load player data from session storage
  useEffect(() => {
    const assessmentData = sessionStorage.getItem('assessmentData');
    if (assessmentData) {
      const data = JSON.parse(assessmentData);
      setPlayerName(data.playerName || 'Player');
      setGameState('test');
    } else {
      // No assessment data, redirect to assessment page
      navigate('/assessment');
    }
  }, [navigate]);

  // Initialize question pool when test starts
  useEffect(() => {
    if (gameState === 'test' && questionPool.length === 0 && questionsLoaded) {
      const allQuestions = questionBank;
      
      // Start with a mix of Level 1 and Level 2 questions
      const level1 = allQuestions.filter((q: Question) => q.difficulty === 1);
      const level2 = allQuestions.filter((q: Question) => q.difficulty === 2);
      
      // Shuffle and select initial questions
      const shuffled = [...level1, ...level2].sort(() => Math.random() - 0.5);
      setQuestionPool(shuffled.slice(0, 5));
    }
  }, [gameState, questionPool.length, questionsLoaded, questionBank]);

  const selectNextQuestion = (currentDifficulty: number, wasCorrect: boolean): Question | null => {
    const allQuestions = questionBank;
    const usedIds = [...answeredQuestions.map(a => a.id), ...questionPool.map(q => q.id)];
    
    // Determine next difficulty level based on performance
    let targetDifficulty = currentDifficulty;
    if (wasCorrect && currentIQ > 85) {
      targetDifficulty = Math.min(5, currentDifficulty + 1);
    } else if (!wasCorrect && currentIQ < 95) {
      targetDifficulty = Math.max(1, currentDifficulty - 1);
    }
    
    // Find available questions at target difficulty
    let availableQuestions = allQuestions.filter(
      (q: Question) => q.difficulty === targetDifficulty && !usedIds.includes(q.id)
    );
    
    // If no questions at target difficulty, try adjacent levels
    if (availableQuestions.length === 0) {
      availableQuestions = allQuestions.filter(
        (q: Question) => Math.abs(q.difficulty - targetDifficulty) === 1 && !usedIds.includes(q.id)
      );
    }
    
    if (availableQuestions.length > 0) {
      const randomQuestion = availableQuestions[Math.floor(Math.random() * availableQuestions.length)];
      return randomQuestion;
    }
    
    return null;
  };

  const handleAnswerSubmit = () => {
    if (selectedAnswer === null) return;

    const currentQuestion = questionPool[currentQuestionIndex];
    const isCorrect = selectedAnswer === currentQuestion.correct;
    
    setLastAnswerCorrect(isCorrect);
    setShowExplanation(true);

    // Calculate IQ adjustment
    let iqAdjustment = 0;
    
    if (isCorrect) {
      iqAdjustment = 3 + (currentQuestion.difficulty * 0.4);
    } else {
      iqAdjustment = -(2 + (5 - currentQuestion.difficulty) * 0.4);
    }
    
    iqAdjustment *= (1 - confidence * 0.1);
    
    const newIQ = Math.max(60, Math.min(150, currentIQ + iqAdjustment));
    setCurrentIQ(newIQ);

    // Update confidence
    const recentAnswers = [...answeredQuestions.slice(-4), { correct: isCorrect }];
    const recentCorrect = recentAnswers.filter((a: any) => a.correct).length;
    const newConfidence = Math.min(1, confidence + 0.08 + (recentCorrect / recentAnswers.length) * 0.05);
    setConfidence(newConfidence);

    // Record the answer
    const answerRecord: AnswerRecord = {
      id: currentQuestion.id,
      question: currentQuestion.question,
      correct: isCorrect,
      difficulty: currentQuestion.difficulty,
      topic: currentQuestion.topic,
      iq_at_time: newIQ
    };
    
    setAnsweredQuestions([...answeredQuestions, answerRecord]);
  };

  const handleNextQuestion = () => {
    const currentQuestion = questionPool[currentQuestionIndex];
    
    // Check if test should end
    const recentQuestions = answeredQuestions.slice(-8);
    const recentCorrect = recentQuestions.filter(q => q.correct).length;
    const recentAccuracy = recentQuestions.length > 0 ? recentCorrect / recentQuestions.length : 0;
    
    // End test if:
    // 1. Answered 60 questions (max)
    // 2. Answered at least 15 questions AND confidence >= 95%
    // 3. Answered at least 20 questions AND recent accuracy is very consistent (>87.5% or <25%)
    const shouldEnd = 
      answeredQuestions.length >= 60 || 
      (answeredQuestions.length >= 15 && confidence >= 0.95) ||
      (answeredQuestions.length >= 20 && (recentAccuracy >= 0.875 || recentAccuracy <= 0.25));
    
    if (shouldEnd) {
      setGameState('results');
      submitTestResults();
      return;
    }

    // Select next question
    const nextQuestion = selectNextQuestion(
      currentQuestion.difficulty, 
      lastAnswerCorrect!
    );
    
    if (nextQuestion) {
      setQuestionPool([...questionPool, nextQuestion]);
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
      setLastAnswerCorrect(null);
    } else {
      setGameState('results');
      submitTestResults();
    }
  };

  const getIQLevel = (iq: number) => {
    if (iq < 76) return { level: 'Foundational', color: 'orange', description: 'Building basic football knowledge' };
    if (iq < 91) return { level: 'Developing', color: 'yellow', description: 'Solid fundamentals with room for growth' };
    if (iq < 111) return { level: 'Proficient', color: 'green', description: 'Strong game knowledge and understanding' };
    if (iq < 131) return { level: 'Advanced', color: 'blue', description: 'Leadership and coaching potential' };
    return { level: 'Expert', color: 'purple', description: 'Exceptional mastery of football rules' };
  };

  const getTopicBreakdown = () => {
    const topics: { [key: string]: { correct: number; total: number } } = {};
    answeredQuestions.forEach(a => {
      if (!topics[a.topic]) {
        topics[a.topic] = { correct: 0, total: 0 };
      }
      topics[a.topic].total++;
      if (a.correct) topics[a.topic].correct++;
    });
    return Object.entries(topics).map(([topic, stats]) => ({
      topic,
      correct: stats.correct,
      total: stats.total,
      accuracy: ((stats.correct / stats.total) * 100).toFixed(0)
    })).sort((a, b) => parseInt(b.accuracy) - parseInt(a.accuracy));
  };

  const submitTestResults = async () => {
    const assessmentData = sessionStorage.getItem('assessmentData');
    if (!assessmentData) return;
    
    const formData = JSON.parse(assessmentData);
    const iqLevel = getIQLevel(currentIQ);
    const accuracy = ((answeredQuestions.filter(a => a.correct).length / answeredQuestions.length) * 100).toFixed(1);
    const topicBreakdown = getTopicBreakdown();
    
    const testData = {
      ...formData,
      ixScore: currentIQ,
      ixLevel: iqLevel.level,
      questionsAnswered: answeredQuestions.length,
      accuracy: parseFloat(accuracy),
      topicBreakdown: topicBreakdown,
      answers: answeredQuestions
    };
    
    try {
      const response = await fetch('/backend/api/submit-test.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(testData)
      });
      
      if (!response.ok) {
        console.error('Failed to submit test results');
      }
    } catch (error) {
      console.error('Error submitting test results:', error);
    }
  };

  const handleFlagQuestion = async () => {
    const currentQuestion = questionPool[currentQuestionIndex];
    if (!flaggedQuestions.includes(currentQuestion.id)) {
      setFlaggedQuestions([...flaggedQuestions, currentQuestion.id]);
      console.log('Question flagged:', currentQuestion.id);
      
      // Send flagged question to backend
      try {
        const assessmentData = sessionStorage.getItem('assessmentData');
        const formData = assessmentData ? JSON.parse(assessmentData) : {};
        
        const response = await fetch('/backend/api/flag-question.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            questionId: currentQuestion.id,
            playerName: formData.playerName || 'Anonymous',
            playerEmail: formData.playerEmail || null,
            reason: 'Question flagged during test'
          })
        });
        
        if (response.ok) {
          alert('Question flagged for review. Thank you for your feedback!');
        } else {
          console.error('Failed to submit flag');
          alert('Question flagged locally. Thank you for your feedback!');
        }
      } catch (error) {
        console.error('Error flagging question:', error);
        alert('Question flagged locally. Thank you for your feedback!');
      }
    }
  };

  const restartTest = () => {
    navigate('/');
  };

  if (gameState === 'loading') {
    return (
      <div className="test-page">
        <div className="test-container">
          <p>Loading test...</p>
        </div>
      </div>
    );
  }

  // Test Screen
  if (gameState === 'test' && questionPool.length > 0 && currentQuestionIndex < questionPool.length) {
    const currentQuestion = questionPool[currentQuestionIndex];
    const progress = ((answeredQuestions.length) / Math.min(25, answeredQuestions.length + 15)) * 100;
    const iqLevel = getIQLevel(currentIQ);

    return (
      <div className="test-page">
        <div className="test-container">
          {/* Header */}
          <div className="test-header">
            <div className="test-player-info">
              <h2>{playerName}'s Assessment</h2>
              <p>Question {answeredQuestions.length + 1}</p>
            </div>
            <div className="test-score">
              <div className="iq-score">{Math.round(currentIQ)}</div>
              <div className={`iq-level iq-level-${iqLevel.color}`}>{iqLevel.level}</div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="progress-section">
            <div className="progress-labels">
              <span>Progress</span>
              <span>{Math.round(confidence * 100)}% confidence</span>
            </div>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progress}%` }}></div>
            </div>
          </div>

          {/* Question Card */}
          <div className="question-card">
            <div className="question-header">
              <span className="question-badge">
                Level {currentQuestion.difficulty} • {currentQuestion.topic}
              </span>
              <button 
                onClick={handleFlagQuestion} 
                className="btn-flag"
                title="Flag this question for review"
              >
                🚩 Flag Question
              </button>
            </div>
            <h3 className="question-text">{currentQuestion.question}</h3>
            {currentQuestion.image && (
              <div className="question-image">
                <img src={currentQuestion.image} alt="Football diagram" />
              </div>
            )}

            <div className="question-content">
              {!showExplanation ? (
                <>
                  <div className="options-list">
                    {currentQuestion.options.map((option, index) => (
                      <label
                        key={index}
                        className={`option-item ${selectedAnswer === index ? 'selected' : ''}`}
                      >
                        <input
                          type="radio"
                          name="answer"
                          value={index}
                          checked={selectedAnswer === index}
                          onChange={() => setSelectedAnswer(index)}
                        />
                        <span className="option-text">{option}</span>
                      </label>
                    ))}
                  </div>
                  <button
                    onClick={handleAnswerSubmit}
                    disabled={selectedAnswer === null}
                    className="btn-submit"
                  >
                    Submit Answer
                  </button>
                </>
              ) : (
                <>
                  <div className={`explanation-box ${lastAnswerCorrect ? 'correct' : 'incorrect'}`}>
                    <div className="explanation-header">
                      {lastAnswerCorrect ? '✓ Correct!' : '✗ Incorrect'}
                    </div>
                    <p className="explanation-text">{currentQuestion.explanation}</p>
                    <p className="correct-answer">
                      Correct answer: {currentQuestion.options[currentQuestion.correct]}
                    </p>
                  </div>
                  <button onClick={handleNextQuestion} className="btn-next">
                    Next Question
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Results Screen
  if (gameState === 'results') {
    const iqLevel = getIQLevel(currentIQ);
    const accuracy = ((answeredQuestions.filter(a => a.correct).length / answeredQuestions.length) * 100).toFixed(1);
    const topicBreakdown = getTopicBreakdown();

    return (
      <div className="test-page results-page">
        <div className="test-container">
          <div className="results-header">
            <h1>Assessment Complete!</h1>
            <p>{playerName}'s IX Test Results</p>
          </div>

          <div className="results-grid">
            <div className="result-card main-score">
              <h2>IX Score</h2>
              <div className="big-score">{Math.round(currentIQ)}</div>
              <div className={`score-level level-${iqLevel.color}`}>{iqLevel.level}</div>
              <p>{iqLevel.description}</p>
            </div>

            <div className="result-card">
              <h3>Performance Summary</h3>
              <div className="stat-row">
                <span>Questions Answered:</span>
                <strong>{answeredQuestions.length}</strong>
              </div>
              <div className="stat-row">
                <span>Overall Accuracy:</span>
                <strong>{accuracy}%</strong>
              </div>

            </div>

            <div className="result-card">
              <h3>Performance by Topic</h3>
              <div className="topic-list">
                <div className="topic-row">
                  <span>Topic</span>
                  <span>Correct</span>
                  <span>Total</span>
                  <span>Accuracy</span>
                </div>
                {topicBreakdown.map((t, i) => (
                  <div key={i} className="topic-row">
                    <span>{t.topic}</span>
                    <span className="topic-stats">
                      <span className="topic-correct">{t.correct}</span>
                      <span className="topic-total">{t.total}</span>
                      <span className="topic-score">{t.accuracy}%</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="results-actions">
            <p className="results-note">
              Coach Mesa will review your results and contact you within 48 hours.
            </p>
            <button onClick={restartTest} className="btn-home">
              Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default FootballIQTest;

